import { useMemo } from 'react';
import { useQuery, UseQueryOptions } from 'react-query';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { CustomQueryOptions, FetchError } from 'src/types/TApiQueries';
import { getQueueStats, QueueStats } from '../functions/queueApi';
import { queryFnFetchErrorWrapper, throwFetchError } from '../utils/errorHandlers';
import { mergeQueryOptions } from '../utils/helpers';
import { queryNames } from '../utils/queryNames';

export function useQueueStatsQuery(
  options?: CustomQueryOptions,
  queryOptions?: UseQueryOptions<QueueStats[], FetchError>,
) {
  const actualQueryOptions = mergeQueryOptions<QueueStats[]>(options, queryOptions);
  const accessToken = useAccessToken();

  const fetchFn = async (token: string) => {
    const result = (await getQueueStats(token)) as any;
    // The API returns { timestamp, hostname, queues: [...] }
    // Handle both wrapped (result.data.queues) and unwrapped (result.queues) responses
    if (result?.queues) {
      return result.queues;
    }
    if (result?.data?.queues) {
      return result.data.queues;
    }
    return [];
  };

  const queryFn = useMemo(() => queryFnFetchErrorWrapper(() => fetchFn(accessToken)), [accessToken]);

  const query = useQuery<QueueStats[], FetchError>([queryNames.queueStats], queryFn, actualQueryOptions);

  if (options?.handleErrors && query.status === 'error') {
    if (query.isRefetchError) {
      // Sentry.captureMessage('Failed refetching queue stats', Sentry.Severity.Error);
    } else {
      throwFetchError(query.error);
    }
  }
  return query;
}
